import type { IAuthorizeResponse, ICallbackResponse, ILogoutResponse, IRefreshResponse, IUser } from '#interfaces/auth'
import { API_URL } from '#lib/config'
import i18n from '#lib/i18n'

export class AuthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public isTokenExpired: boolean = false,
  ) {
    super(message)
    this.name = 'AuthApiError'
  }
}

interface IErrorBody {
  message?: string
}

interface IRawCompanyRef {
  id?: string
  name?: string
}

interface IRawCallbackUser {
  user?: {
    company?: IRawCompanyRef
    selectedCompany?: IRawCompanyRef | null
  }
}

function isErrorBody(value: unknown): value is IErrorBody {
  return typeof value === 'object' && value !== null
}

/** Extract error message from a failed auth response and throw an AuthApiError. */
async function throwAuthError(response: Response, fallback: string, isTokenExpired = false): Promise<never> {
  const body: unknown = await response.json().catch(() => ({}))
  const message = isErrorBody(body) ? body.message : undefined
  throw new AuthApiError(message ?? fallback, response.status, isTokenExpired)
}

/** Parse response JSON — auth endpoints are outside the OpenAPI schema, so runtime type safety is best-effort. */
async function parseJson<T>(response: Response): Promise<T> {
  const data: unknown = await response.json()
  return data as T
}

/**
 * Initiate OAuth2 Authorization Code + PKCE flow.
 * Returns the Keycloak authorization URL and state for CSRF protection.
 */
export async function authorize(codeChallenge: string): Promise<IAuthorizeResponse> {
  const redirectUri = import.meta.env.REACT_APP_AUTH_REDIRECT_URI
  const response = await fetch(`${API_URL}/auth/authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language },
    body: JSON.stringify({
      code_challenge: codeChallenge,
      ...(redirectUri ? { redirect_uri: redirectUri } : {}),
    }),
  })

  if (!response.ok) await throwAuthError(response, 'Failed to initiate OAuth')

  return parseJson<IAuthorizeResponse>(response)
}

/**
 * Exchange authorization code for tokens (callback from Keycloak).
 */
export async function callback(code: string, codeVerifier: string, state: string): Promise<ICallbackResponse> {
  const response = await fetch(`${API_URL}/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language },
    body: JSON.stringify({ code, code_verifier: codeVerifier, state }),
    credentials: 'include',
  })

  if (!response.ok) await throwAuthError(response, 'Failed to exchange code for tokens', response.status === 401)

  const data = await parseJson<ICallbackResponse & IRawCallbackUser>(response)

  // selectedCompany is the stored choice. user.company on this payload is the home company.
  const selectedCompany = data.user?.selectedCompany
  const company = selectedCompany?.id ? selectedCompany : data.user?.company
  if (company?.id) data.user.companyId = company.id
  if (company?.name) data.user.companyName = company.name

  return data
}

/**
 * Refresh the access token using the HttpOnly refresh token cookie.
 */
export async function refresh(): Promise<IRefreshResponse> {
  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language },
    credentials: 'include',
  })

  if (!response.ok) throw new AuthApiError('Token refresh failed', response.status, true)

  return parseJson<IRefreshResponse>(response)
}

/**
 * Fetch the currently authenticated user.
 */
export async function me(token: string, switchUserEmail?: string): Promise<IUser> {
  let url = `${API_URL}/auth/me`
  if (switchUserEmail) url += `?_switch_user=${encodeURIComponent(switchUserEmail)}`

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, 'Accept-Language': i18n.language },
    credentials: 'include',
  })

  if (!response.ok) throw new AuthApiError('Failed to fetch user', response.status, response.status === 401)

  const data = await parseJson<IUser & { user?: { company?: { id?: string; name?: string } } }>(response)
  // Extract companyId and companyName from the nested user.company object
  if (data.user?.company?.id) data.companyId = data.user.company.id
  if (data.user?.company?.name) data.companyName = data.user.company.name

  return data
}

/**
 * Logout from the application.
 * Returns the Keycloak logout URL for full SSO logout.
 */
export async function logout(token: string): Promise<ILogoutResponse | null> {
  try {
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Accept-Language': i18n.language },
      credentials: 'include',
    })

    if (response.ok) return parseJson<ILogoutResponse>(response)

    return null
  } catch {
    return null
  }
}
